/**
 * ChartFrame (Phase 3, §6.4) — SPINE. The accessible wrapper every chart renders
 * inside: a titled figure, a responsive `role="img"` SVG carrying `<title>` +
 * `<desc>`, an optional legend, and a "Tabelle anzeigen" disclosure revealing
 * the SAME data as an accessible table (the mandated non-colour fallback).
 *
 * Theme-aware chrome comes from CSS custom properties on `.viz-root`, driven by
 * BOTH `prefers-color-scheme` AND `:root[data-theme]` (the app theme toggle wins
 * either way). The style block is hoisted + de-duplicated by React 19 via its
 * `href`/`precedence` props, so many charts share one `<style>`.
 */
import { useId, useState, type ReactNode } from 'react';
import { useChartDims, type ChartDims } from './useChartDims.tsx';

const VIZ_CSS = `
.viz-root{
  --viz-axis:#94a3b8;--viz-grid:#f1f5f9;--viz-label:#475569;--viz-label-muted:#94a3b8;
  --viz-surface:#ffffff;--viz-tooltip-bg:#ffffff;--viz-tooltip-border:rgba(15,23,42,0.05);
  --viz-tooltip-fg:#0f172a;--viz-baseline:#cbd5e1;
}
@media (prefers-color-scheme:dark){
  .viz-root{
    --viz-axis:#475569;--viz-grid:rgba(30,41,59,0.7);--viz-label:#94a3b8;--viz-label-muted:#64748b;
    --viz-surface:#020617;--viz-tooltip-bg:#0f172a;--viz-tooltip-border:rgba(255,255,255,0.1);
    --viz-tooltip-fg:#f1f5f9;--viz-baseline:#334155;
  }
}
:root[data-theme='light'] .viz-root{
  --viz-axis:#94a3b8;--viz-grid:#f1f5f9;--viz-label:#475569;--viz-label-muted:#94a3b8;
  --viz-surface:#ffffff;--viz-tooltip-bg:#ffffff;--viz-tooltip-border:rgba(15,23,42,0.05);
  --viz-tooltip-fg:#0f172a;--viz-baseline:#cbd5e1;
}
:root[data-theme='dark'] .viz-root{
  --viz-axis:#475569;--viz-grid:rgba(30,41,59,0.7);--viz-label:#94a3b8;--viz-label-muted:#64748b;
  --viz-surface:#020617;--viz-tooltip-bg:#0f172a;--viz-tooltip-border:rgba(255,255,255,0.1);
  --viz-tooltip-fg:#f1f5f9;--viz-baseline:#334155;
}
`;

export interface ChartTable {
  columns: string[];
  rows: (string | number)[][];
  caption?: string;
}

export interface ChartFrameProps {
  title: string;
  subtitle?: string;
  /** SVG `<desc>` — a one-line summary for assistive tech. Defaults to subtitle/title. */
  description?: string;
  legend?: ReactNode;
  /** The visually-hidden-until-expanded data mirror. */
  table: ChartTable;
  /** Height as a fraction of width when `height` is absent. */
  aspect?: number;
  height?: number;
  /** Force a minimum SVG width (px); the frame scrolls horizontally beyond it. */
  scrollMinWidth?: number;
  /** Chart marks; receives the resolved plot dims. */
  children: (dims: ChartDims) => ReactNode;
}

export function ChartFrame({
  title,
  subtitle,
  description,
  legend,
  table,
  aspect = 0.5,
  height,
  scrollMinWidth,
  children,
}: ChartFrameProps) {
  const { ref, dims } = useChartDims({ aspect, height });
  const [tableOpen, setTableOpen] = useState(false);
  const titleId = useId();
  const descId = useId();
  const desc = description ?? subtitle ?? title;

  const width = Math.max(dims.width, scrollMinWidth ?? 0);
  const plot: ChartDims = { width, height: dims.height };

  return (
    <figure className="viz-root m-0 flex flex-col gap-3">
      <style href="finance-viz" precedence="medium">
        {VIZ_CSS}
      </style>

      <figcaption className="flex flex-col gap-0.5">
        <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</span>
        {subtitle ? (
          <span className="text-xs text-slate-400 dark:text-slate-500">{subtitle}</span>
        ) : null}
      </figcaption>

      <div ref={ref} className="w-full overflow-x-auto">
        <svg
          role="img"
          aria-labelledby={`${titleId} ${descId}`}
          width={width}
          height={dims.height}
          viewBox={`0 0 ${width} ${dims.height}`}
          preserveAspectRatio="xMidYMid meet"
        >
          <title id={titleId}>{title}</title>
          <desc id={descId}>{desc}</desc>
          {children(plot)}
        </svg>
      </div>

      {legend}

      <details
        className="text-xs"
        onToggle={(e) => setTableOpen((e.currentTarget as HTMLDetailsElement).open)}
      >
        <summary className="cursor-pointer select-none font-medium text-sky-600 transition-colors duration-150 hover:text-sky-800 dark:text-sky-400 dark:hover:text-sky-300">
          {tableOpen ? 'Tabelle ausblenden' : 'Tabelle anzeigen'}
        </summary>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full border-collapse text-left text-xs">
            {table.caption ? (
              <caption className="sr-only">{table.caption}</caption>
            ) : null}
            <thead>
              <tr className="border-b border-slate-100 dark:border-slate-800/70">
                {table.columns.map((col, i) => (
                  <th
                    key={col}
                    scope="col"
                    className={`px-2 py-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500 ${
                      i === 0 ? 'text-left' : 'text-right'
                    }`}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800/70">
              {table.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td
                      key={c}
                      className={`px-2 py-1.5 text-slate-600 dark:text-slate-400 ${
                        c === 0 ? 'text-left' : 'text-right tabular-nums'
                      }`}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </figure>
  );
}
